import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ExternalLink, ShieldAlert, CheckCircle2, X, Award, Sparkles, RefreshCw } from "lucide-react";

/**
 * Stripe / billing panel for the CRM dialog. Loads the detail blob from
 * /admin/companies/{cid}/billing-detail and renders:
 *   - status + access pills
 *   - read-only Stripe IDs (with link-out to Stripe Dashboard)
 *   - manual override controls (with required reason)
 *   - founding customer grant / revoke
 *
 * All mutations require a non-empty reason that is captured in the audit log.
 */
const STRIPE_DASHBOARD = "https://dashboard.stripe.com/test";   // test mode for preview

const ACCESS_PILL = {
  full:       "bg-emerald-100 text-emerald-800",
  restricted: "bg-red-100 text-red-700",
  locked:     "bg-red-200 text-red-800",
};

const STATUS_PILL = {
  active:        "bg-emerald-100 text-emerald-800",
  trial:         "bg-amber-100 text-amber-800",
  payment_issue: "bg-amber-100 text-amber-800",
  cancelled:     "bg-slate-200 text-slate-600",
  expired:       "bg-orange-100 text-orange-800",
  incomplete:    "bg-slate-100 text-slate-600",
  suspended:     "bg-red-100 text-red-700",
};

const OVERRIDE_CHOICES = [
  { value: "active",            label: "Force Active" },
  { value: "founding_customer", label: "Force Founding Customer" },
  { value: "trial",             label: "Force Trial" },
  { value: "suspended",         label: "Suspend" },
  { value: "cancelled",         label: "Cancel" },
  { value: "expired",           label: "Mark Expired" },
  { value: "clear",             label: "Clear override" },
];

function Field({ label, value, mono, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[#4B5773]">{label}</div>
      <div className={`mt-0.5 text-sm text-[#0B1A36] ${mono ? "font-mono break-all" : ""}`}>{value ?? "-"}</div>
    </div>
  );
}

export default function BillingPanel({ companyId, onMutated }) {
  const [detail, setDetail] = useState(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [foundingOpen, setFoundingOpen] = useState(null);  // "grant" | "revoke" | null

  const load = async () => {
    try {
      const r = await api.get(`/admin/companies/${companyId}/billing-detail`);
      setDetail(r.data);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail));
    }
  };
  // Reload whenever the parent rotates companyId.
  useEffect(() => { if (companyId) load(); /* eslint-disable-next-line */ }, [companyId]);

  if (!detail) return <div className="py-6 text-sm text-[#4B5773]">Loading billing detail…</div>;
  const c = detail.company;
  const access = detail.access;
  const fp = detail.founding_programme;

  const customerUrl = c.stripe_customer_id ? `${STRIPE_DASHBOARD}/customers/${c.stripe_customer_id}` : null;
  const subUrl = c.stripe_subscription_id ? `${STRIPE_DASHBOARD}/subscriptions/${c.stripe_subscription_id}` : null;

  return (
    <div className="space-y-6" data-testid="admin-billing-panel">
      {/* Pills row */}
      <div className="flex flex-wrap gap-2">
        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${STATUS_PILL[c.billing_status] || "bg-slate-100 text-slate-700"}`}
              data-testid="admin-billing-status-pill">
          Billing: {c.billing_status || "-"}
        </span>
        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${ACCESS_PILL[access.access] || "bg-slate-100 text-slate-700"}`}
              data-testid="admin-access-pill">
          Access: {access.access}
        </span>
        {c.status_override && (
          <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-800 border border-red-200"
                data-testid="admin-override-pill">
            <ShieldAlert className="inline h-3 w-3 mr-0.5"/>Override: {c.status_override}
          </span>
        )}
        {c.membership_type === "founding_customer" && c.founding_member_number && (
          <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-[#0B1A36] text-white"
                data-testid="admin-founding-pill">
            <Sparkles className="inline h-3 w-3 mr-0.5"/>Founding #{c.founding_member_number} / {fp.limit}
          </span>
        )}
      </div>

      {/* Main billing grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 rounded-lg border border-slate-200 p-4 bg-white">
        <Field label="Membership type" value={(c.membership_type || "-").replace("_", " ")} testId="admin-bd-membership"/>
        <Field label="Billing status" value={c.billing_status || "-"} testId="admin-bd-bill-status"/>
        <Field label="Current plan" value={c.plan_label || (c.plan_slug ? c.plan_slug : "-")} testId="admin-bd-plan"/>
        <Field label="Trial start" value={c.trial_start_date} testId="admin-bd-trial-start"/>
        <Field label="Trial end"   value={c.trial_end_date}   testId="admin-bd-trial-end"/>
        <Field label="Renews on"   value={c.current_period_end?.slice(0,10)} testId="admin-bd-renews"/>
        <Field label="Last payment status" value={c.last_payment_status || "-"} testId="admin-bd-paystat"/>
        <Field label="Last payment date"   value={c.last_payment_date?.slice(0,10)} testId="admin-bd-paydate"/>
        <Field label="Cancel at period end" value={c.cancel_at_period_end ? "Yes" : "No"} testId="admin-bd-cancelatpe"/>
        {c.cancel_at_period_end && (
          <Field label="Cancellation date" value={(c.cancel_at || c.current_period_end)?.slice(0,10) || "-"} testId="admin-bd-cancel-at"/>
        )}
      </div>

      {/* Stripe IDs */}
      <div className="rounded-lg border border-slate-200 p-4 bg-white">
        <div className="text-xs uppercase tracking-widest text-[#4B5773] mb-3">Stripe</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#4B5773]">Customer ID</div>
            <div className="font-mono text-xs text-[#0B1A36] break-all mt-1" data-testid="admin-bd-cust-id">{c.stripe_customer_id || "-"}</div>
            {customerUrl && (
              <a href={customerUrl} target="_blank" rel="noreferrer"
                 data-testid="admin-bd-open-customer"
                 className="mt-2 inline-flex items-center gap-1 text-xs text-[#2563EB] hover:underline">
                Open Customer in Stripe <ExternalLink className="h-3 w-3"/>
              </a>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#4B5773]">Subscription ID</div>
            <div className="font-mono text-xs text-[#0B1A36] break-all mt-1" data-testid="admin-bd-sub-id">{c.stripe_subscription_id || "-"}</div>
            {subUrl && (
              <a href={subUrl} target="_blank" rel="noreferrer"
                 data-testid="admin-bd-open-sub"
                 className="mt-2 inline-flex items-center gap-1 text-xs text-[#2563EB] hover:underline">
                Open Subscription in Stripe <ExternalLink className="h-3 w-3"/>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Active override callout */}
      {c.status_override && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4" data-testid="admin-active-override">
          <div className="flex items-start gap-2">
            <ShieldAlert className="h-4 w-4 text-red-700 mt-0.5"/>
            <div className="flex-1 text-sm">
              <div className="font-semibold text-red-800">Manual override active: {c.status_override}</div>
              <div className="text-red-700 text-xs mt-1">
                Reason: {c.status_override_reason || "-"} · By {c.status_override_by?.name || c.status_override_by?.email || "-"} on {c.status_override_at?.slice(0,10)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {c.stripe_subscription_id && (
          <Button size="sm" variant="outline"
                  data-testid="admin-refresh-stripe-btn"
                  onClick={async () => {
                    try {
                      await api.post(`/admin/companies/${companyId}/refresh-from-stripe`);
                      toast.success("Refreshed from Stripe");
                      await load();
                      onMutated?.();
                    } catch (e) {
                      toast.error(formatError(e?.response?.data?.detail));
                    }
                  }}
                  className="rounded-lg border-slate-200">
            <RefreshCw className="h-4 w-4 mr-1.5"/>Refresh from Stripe
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={()=>setOverrideOpen(true)}
                data-testid="admin-open-override-btn" className="rounded-lg border-slate-200">
          <ShieldAlert className="h-4 w-4 mr-1.5"/>Manual override
        </Button>
        {c.membership_type === "founding_customer" ? (
          <Button size="sm" variant="outline" onClick={()=>setFoundingOpen("revoke")}
                  data-testid="admin-revoke-founding-btn" className="rounded-lg border-slate-200 text-red-700">
            <X className="h-4 w-4 mr-1.5"/>Revoke Founding
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={()=>setFoundingOpen("grant")}
                  data-testid="admin-grant-founding-btn" className="rounded-lg border-slate-200">
            <Award className="h-4 w-4 mr-1.5"/>Grant Founding
          </Button>
        )}
      </div>

      {/* Founding programme summary */}
      {c.membership_type === "founding_customer" && (
        <div className="rounded-lg border border-slate-200 p-4 bg-white text-sm" data-testid="admin-founding-details">
          <div className="text-xs uppercase tracking-widest text-[#4B5773] mb-2">Founding Customer</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Member number" value={c.founding_member_number ? `#${c.founding_member_number} / ${fp.limit}` : "-"} testId="admin-fd-num"/>
            <Field label="Assigned by" value={c.founding_assigned_by === "manual" ? "Manual (Super Admin)" : "Auto"} testId="admin-fd-by"/>
            {c.founding_granted_at && <Field label="Granted on" value={c.founding_granted_at.slice(0,10)} testId="admin-fd-granted"/>}
            {c.founding_granted_by && <Field label="Granted by" value={c.founding_granted_by.name || c.founding_granted_by.email} testId="admin-fd-grantby"/>}
          </div>
        </div>
      )}

      <OverrideDialog open={overrideOpen} onClose={()=>setOverrideOpen(false)} companyId={companyId}
                      current={c.status_override} onDone={()=>{ load(); onMutated?.(); }}/>
      <FoundingDialog mode={foundingOpen} onClose={()=>setFoundingOpen(null)} companyId={companyId}
                      onDone={()=>{ load(); onMutated?.(); }}/>
    </div>
  );
}

function OverrideDialog({ open, onClose, companyId, current, onDone }) {
  const [choice, setChoice] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setChoice(""); setReason(""); setNotes(""); } }, [open]);
  if (!open) return null;

  const submit = async () => {
    if (!choice) { toast.error("Pick an override action."); return; }
    if (!reason.trim()) { toast.error("Reason is required."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/companies/${companyId}/billing-override`,
        { status_override: choice, reason: reason.trim(), notes: notes.trim() || undefined });
      toast.success(choice === "clear" ? "Override cleared." : `Override applied: ${choice}`);
      onDone();
      onClose();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="admin-override-dialog">
      <div className="bg-white rounded-xl max-w-md w-full p-5 space-y-4">
        <div className="font-display text-xl">Manual billing override</div>
        {current && (
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">
            Currently overridden to <span className="font-semibold">{current}</span>. Choose &quot;Clear override&quot; to remove it.
          </div>
        )}
        <div>
          <Label className="eyebrow">Action *</Label>
          <Select value={choice} onValueChange={setChoice}>
            <SelectTrigger data-testid="admin-override-choice" className="mt-1.5 h-11 rounded-lg"><SelectValue placeholder="Choose an action"/></SelectTrigger>
            <SelectContent>{OVERRIDE_CHOICES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="eyebrow">Reason *</Label>
          <Input data-testid="admin-override-reason" value={reason} onChange={(e)=>setReason(e.target.value)}
                 placeholder="Short reason - shown in audit log"
                 className="mt-1.5 h-11 rounded-lg"/>
        </div>
        <div>
          <Label className="eyebrow">Notes (optional)</Label>
          <textarea data-testid="admin-override-notes" value={notes} onChange={(e)=>setNotes(e.target.value)} rows={3}
                    placeholder="Anything else worth recording"
                    className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"/>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-lg border-slate-200">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="admin-override-submit"
                  className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
            {busy ? "Saving…" : <><CheckCircle2 className="h-4 w-4 mr-1.5"/>Apply</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FoundingDialog({ mode, onClose, companyId, onDone }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (mode) { setReason(""); setNotes(""); } }, [mode]);
  if (!mode) return null;
  const isGrant = mode === "grant";

  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason is required."); return; }
    setBusy(true);
    try {
      const ep = isGrant ? "grant-founding" : "revoke-founding";
      await api.post(`/admin/companies/${companyId}/${ep}`,
        { reason: reason.trim(), notes: notes.trim() || undefined });
      toast.success(isGrant ? "Founding Customer granted." : "Founding Customer revoked.");
      onDone();
      onClose();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="admin-founding-dialog">
      <div className="bg-white rounded-xl max-w-md w-full p-5 space-y-4">
        <div className="font-display text-xl">{isGrant ? "Grant Founding Customer" : "Revoke Founding Customer"}</div>
        <div className="text-xs text-[#4B5773]">
          {isGrant
            ? "Assigns the next available Founding Member number even if the public cap is reached. Sets a 3-month free window."
            : "Removes Founding Customer status and moves the company to a 14-day trial. The freed slot becomes available."}
        </div>
        <div>
          <Label className="eyebrow">Reason *</Label>
          <Input data-testid="admin-founding-reason" value={reason} onChange={(e)=>setReason(e.target.value)}
                 placeholder="e.g. Partnership programme" className="mt-1.5 h-11 rounded-lg"/>
        </div>
        <div>
          <Label className="eyebrow">Notes (optional)</Label>
          <textarea data-testid="admin-founding-notes" value={notes} onChange={(e)=>setNotes(e.target.value)} rows={3}
                    className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"/>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-lg border-slate-200">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="admin-founding-submit"
                  className={`rounded-lg ${isGrant ? "bg-[#2563EB] hover:bg-[#1D4ED8]" : "bg-red-600 hover:bg-red-700"}`}>
            {busy ? "Saving…" : isGrant ? "Grant" : "Revoke"}
          </Button>
        </div>
      </div>
    </div>
  );
}
